import { useContext, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { trackTableReferenceClicked } from '@/analytics/utils/table-analytics'
import { ActiveTableReferenceContext, TableReferenceContext } from './TableReferenceContext'
import { tableReferenceHref, tableReferenceLabel, type TableReferenceTarget } from '../reference'

export default function TableReferenceLink({
  reference,
  matterId,
  children,
  className = 'text-brand-600 underline',
}: {
  reference: TableReferenceTarget
  matterId: string
  children: ReactNode
  className?: string
}) {
  const openReference = useContext(TableReferenceContext)
  const activeReferenceId = useContext(ActiveTableReferenceContext)
  const { tableId } = useParams()
  const { trackEvent } = useAnalytics()
  const active = Boolean(reference.id && reference.id === activeReferenceId)
  const label = tableReferenceLabel(reference)
  const title = `${label}${reference.excerpt ? ` — ${reference.excerpt}` : ''}`
  const href = tableReferenceHref(reference, matterId)
  if (!href)
    return (
      <span className={className} title={`${title} (document unavailable)`} aria-label={`${label} (document unavailable)`}>
        {children}
      </span>
    )
  return (
    <a
      onClick={(event) => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
        const openedBesideTable = openReference?.(reference) ?? false
        if (openedBesideTable && tableId && reference.id) trackTableReferenceClicked(trackEvent, tableId, reference.id)
        if (openedBesideTable) event.preventDefault()
      }}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={title}
      data-active={active}
      aria-current={active ? 'true' : undefined}
      className={`${className} cursor-pointer rounded hover:bg-brand-neutral-300 hover:text-brand-neutral-950 data-[active=true]:bg-brand-200 data-[active=true]:border-brand-400 data-[active=true]:text-brand-900`}
      aria-label={label}
    >
      {children}
    </a>
  )
}
